/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { CommonModule } from '@angular/common';
import {
  type GraphTooltipParams,
  InsightsDataService,
} from '../../insights-data.service';
import { Subject, takeUntil } from 'rxjs';
import { latencyGraph } from '../../insights-data.interface';
import { AccessibilityService } from '../../../../../../core/services/accessibility.service';
@Component({
  selector: 'app-latency-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './latency-graph.component.html',
})
export class LatencyGraphComponent implements OnDestroy {
  showTrendLine = false;
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  private readonly destroy$ = new Subject<void>();
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 10;
  behaviorId = 0;
  private readonly printTextColor = '#000000';
  private currentGraphData: latencyGraph | null = null;
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  @Input() set graphData(latencyData: latencyGraph) {
    this.currentGraphData = latencyData;
    this.behaviorId = latencyData.caseBehaviorId ?? 0;
    this.showTrendLine = latencyData.showTrendLine ?? false;
    this.setGraphOptionsDefault(latencyData, false);
    this.setGraphOptionsDefault(latencyData, true);
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly insightsService: InsightsDataService,
    private readonly accessibilityService: AccessibilityService
  ) {
    this.accessibilityService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyThemeToExistingCharts();
      });
  }

  setGraphOptionsDefault(latencyData: latencyGraph, showDaysInterval: boolean) {
    if (showDaysInterval) {
      this.renderPrintChart(latencyData);
      return;
    }

    this.renderMainChart(latencyData);
  }

  private renderPrintChart(latencyData: latencyGraph): void {
    this.chartInstances = [];
    this.totalCharts = 1;
    this.allChartsRendered = false;
    this.renderedChartsCount = 0;
    this.weekGraphData = [];
    const weekData = latencyData;
    const isAllDataNull =
      weekData.averageLatency?.every(val => val === null) &&
      weekData.frequency.every(val => val === null) &&
      weekData.treatmentPhases.length === 0;

    this.weekGraphData.push(weekData);
    const graphOptions = this.setGraphOptions(weekData, true);
    setTimeout(() => {
      const chartDom = document.getElementById(
        `chart-container-latency-${this.behaviorId}-${0}`
      );
      if (chartDom) {
        const chartInstance = echarts.init(chartDom);
        chartInstance.setOption(graphOptions);

        chartInstance.on('finished', () => {
          this.renderedChartsCount++;
          if (this.renderedChartsCount === this.totalCharts) {
            this.allChartsRendered = true;

            this.exportAllGraphs();
          }
        });
        if (!isAllDataNull) {
          this.chartInstances.push(chartInstance);
        }
      }
    }, 100);
  }

  private renderMainChart(latencyData: latencyGraph): void {
    const chartOptions = this.setGraphOptions(latencyData);

    const latencyDom = this.elementRef.nativeElement.querySelector('#latency');

    if (latencyDom) {
      const chartInstance = echarts.init(latencyDom);
      this.chartInstance = chartInstance;
      chartInstance.setOption(chartOptions);
      chartInstance.on('finished', () => {
        this.emitChartAsImage();
      });
    }
  }

  setGraphOptions(latencyData: latencyGraph, forPrint = false) {
    const hasDataY0 = latencyData.averageLatency?.some(
      val => val !== null && val !== undefined
    );
    const textColor = forPrint
      ? this.printTextColor
      : this.getThemeChartTextColor();

    const trendlineLatency = this.insightsService.generateTrendlineArray(
      latencyData.xLabels,
      latencyData.averageLatency || []
    );
    const trendlineFrequency = this.insightsService.generateTrendlineArray(
      latencyData.xLabels,
      latencyData.frequency
    );

    const markLines = latencyData.treatmentPhases?.map(item => {
      const color = this.insightsService.getRandomColor();
      return {
        xAxis: item.treatmentPhaseDate,
        label: {
          show: true,
          formatter: item.treatmentPhaseName,
          position: 'insideEndTop',
          color,
        },
        lineStyle: {
          type: 'solid',
          color,
          width: 2,
        },
        tooltip: {
          show: true,
          formatter: () =>
            `Treatment Phase <br>${item.dateLabel} - ${item.treatmentPhaseName}`,
        },
      };
    });

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const latencyGraphOption = {
      title: {
        text: 'Latency',
        left: 'center',
        textStyle: {
          color: textColor,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: GraphTooltipParams) =>
          this.insightsService.formatItemTooltipWithWeekday(params),
      },
      legend: {
        data: [
          'Average Latency',
          'Frequency',
          'Trendline Latency',
          'Trendline Frequency',
        ],
        left: 'left',
        bottom: 40,
        selectedMode: false,
        textStyle: {
          color: textColor,
        },
        pageTextStyle: {
          color: textColor,
        },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: latencyData.xLabels,
        axisLabel: {
          color: textColor,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: hasDataY0 ? 'Average Latency' : '',
          splitLine: { show: hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: {
            padding: [0, 0, 10, 0],
            color: textColor,
          },
          minInterval: 1,
          axisLabel: {
            color: textColor,
            formatter: (value: number) => Math.floor(value).toString(),
          },
        },
        {
          type: 'value',
          name: 'Frequency',
          position: 'right',
          splitLine: { show: !hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: { padding: [15, 0, 0, 0], color: textColor },
          minInterval: 1,
          axisLabel: {
            color: textColor,
            formatter: (value: number) => Math.floor(value).toString(),
          },
        },
      ],
      series: [
        {
          name: 'Average Latency',
          type: 'bar',
          data: latencyData.averageLatency,
          barMaxWidth: 30,
          itemStyle: {
            color: '#47CD89',
            barBorderRadius: [10, 10, 0, 0],
          },
        },
        {
          name: 'Frequency',
          type: 'line',
          data: latencyData.frequency,
          yAxisIndex: 1,
          symbolSize: 12,
          symbol: 'circle',
          showSymbol: true,
          showAllSymbol: true,
          itemStyle: { color: 'orange' },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            fontSize: 10,
          },
          connectNulls: true,
        },
        {
          name: 'Target Line',
          type: 'line',
          markLine: {
            symbol: 'none',
            data: markLines,
          },
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Latency',
                type: 'line',
                data: trendlineLatency,
                yAxisIndex: 0,
                symbol: 'none',
                lineStyle: {
                  color: 'blue',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Frequency',
                type: 'line',
                data: trendlineFrequency,
                yAxisIndex: 1,
                symbol: 'none',
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    return latencyGraphOption;
  }

  private getThemeChartTextColor(): string {
    const colorValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--gray-600')
      .trim();

    return colorValue ? `rgb(${colorValue})` : '#475467';
  }

  private applyThemeToExistingCharts(): void {
    const graphData = this.currentGraphData;
    if (!graphData) {
      return;
    }

    if (this.chartInstance) {
      this.chartInstance.setOption(this.setGraphOptions(graphData), true);
    }

    for (const chart of this.chartInstances) {
      chart.setOption(this.setGraphOptions(graphData, true), true);
    }
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    for (const chartInstance of this.chartInstances) {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    }

    this.allChartImage.emit(images);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
